/** Componentes de uso general en las dos interfaces. */

import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Chip from '@mui/material/Chip'
import CircularProgress from '@mui/material/CircularProgress'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

import { ErrorPeticion } from '../api/cliente'
import type { EstadoDeca } from '../api/tipos'

/**
 * Traduce un error del backend a un mensaje para el operador.
 *
 * Se busca primero por la clave estable del código de error (`app/errores.py`):
 * así el mensaje que ve portería es el que le dice qué le falta, y no el texto
 * técnico del servidor.
 */
export function useMensajeError() {
  const { t } = useTranslation()
  return (error: unknown): string => {
    if (error instanceof ErrorPeticion) {
      const traducido = t(`errores.${error.clave}`, { defaultValue: '' })
      return traducido || error.message || t('errores.generico')
    }
    if (error instanceof Error) return error.message
    return t('errores.generico')
  }
}

export function AvisoError({ error }: { error: unknown }) {
  const mensaje = useMensajeError()
  if (!error) return null
  return (
    <Alert severity="error" sx={{ my: 2 }}>
      {mensaje(error)}
    </Alert>
  )
}

export function Cargando({ texto }: { texto?: string }) {
  const { t } = useTranslation()
  return (
    <Stack direction="row" spacing={2} sx={{ alignItems: 'center', py: 4 }}>
      <CircularProgress size={24} />
      <Typography color="text.secondary">{texto ?? t('app.cargando')}</Typography>
    </Stack>
  )
}

const COLOR_ESTADO: Record<EstadoDeca, 'default' | 'success' | 'info' | 'error' | 'warning'> = {
  BORRADOR: 'default',
  EMITIDO: 'success',
  MODIFICADO: 'info',
  ANULADO: 'error',
  URL_DESACTIVADA: 'warning',
}

export function EtiquetaEstado({ estado }: { estado: EstadoDeca }) {
  const { t } = useTranslation()
  return (
    <Chip
      size="small"
      label={t(`estados.${estado}`)}
      color={COLOR_ESTADO[estado] ?? 'default'}
      variant={estado === 'BORRADOR' ? 'outlined' : 'filled'}
    />
  )
}

/** Par etiqueta-valor, el patrón de lectura de todo el detalle. */
export function Dato({
  etiqueta,
  children,
  ancho,
}: {
  etiqueta: string
  children: ReactNode
  ancho?: number | string
}) {
  return (
    <Box sx={{ minWidth: ancho ?? 180 }}>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
        {etiqueta}
      </Typography>
      <Typography variant="body2" component="div" sx={{ fontWeight: 500 }}>
        {children ?? '—'}
      </Typography>
    </Box>
  )
}

export function Seccion({ titulo, children }: { titulo: string; children: ReactNode }) {
  return (
    <Box sx={{ mb: 3 }}>
      <Typography
        variant="subtitle2"
        sx={{ textTransform: 'uppercase', letterSpacing: '0.06em', mb: 1 }}
        color="text.secondary"
      >
        {titulo}
      </Typography>
      {children}
    </Box>
  )
}

/** Fecha y hora en formato español. Los instantes llegan del backend en UTC. */
export function formatearInstante(valor: string | null | undefined): string {
  if (!valor) return '—'
  const fecha = new Date(valor.endsWith('Z') || valor.includes('+') ? valor : `${valor}Z`)
  if (Number.isNaN(fecha.getTime())) return valor
  return fecha.toLocaleString('es-ES', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

/**
 * Referencia del envío con su número de camión cuando el pedido va en varios
 * (el camión 1 se muestra sin sufijo: es el caso normal de un solo vehículo).
 */
export function referenciaConCamion(documento: {
  referencia_origen: string
  numero_camion?: number
}): string {
  const numero = documento.numero_camion ?? 1
  return numero > 1 ? `${documento.referencia_origen} · camión ${numero}` : documento.referencia_origen
}

export function formatearFecha(valor: string | null | undefined): string {
  if (!valor) return '—'
  const [ano, mes, dia] = valor.split('-')
  return dia ? `${dia}/${mes}/${ano}` : valor
}

export function formatearBytes(valor: number | null | undefined): string {
  if (!valor) return '—'
  if (valor < 1024) return `${valor} B`
  return `${(valor / 1024).toFixed(1)} kB`
}
